import React, { useState, useEffect, useMemo } from 'react';
import { Table as AntTable, Button, Switch, Space, Modal, Input, Select } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { genNumberTable } from '../../../../function/genNumberTable';
import { REGISTER_TASK } from '../../../../data/constant';

const { Option } = Select;

const headerStyle = {
  color: "white",
  fontWeight: "bold",
  backgroundColor: "#4a4a4a",
  textAlign: 'center',
};

const subHeaderStyle = {
  color: "white",
  fontWeight: "normal",
  backgroundColor: "#6a6a6a",
  textAlign: 'center',
};

const Table = ({ data = [], loading = false, onShiftClick, onRoleChange, onExport, adminList = [] }) => {
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [exportType, setExportType] = useState(null); // 'all' or 'admin'
  const [quantity, setQuantity] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  // Optimistic updates for taskType
  const [optimisticUpdates, setOptimisticUpdates] = useState({});

  // Get current taskType with optimistic updates
  const getTaskType = (recordId, originalTaskType) => {
    if (optimisticUpdates[recordId] !== undefined) {
      return optimisticUpdates[recordId];
    }
    return originalTaskType || '';
  };

  const handleRoleToggle = (task, record, checked) => {
    // Optimistic update
    const currentTaskType = getTaskType(record.id, record.taskType);
    let taskTypesArray = currentTaskType ? currentTaskType.split(',').map(t => t.trim()).filter(t => t) : [];
    
    if (checked) {
      if (!taskTypesArray.includes(task)) {
        taskTypesArray.push(task);
      }
    } else {
      taskTypesArray = taskTypesArray.filter(t => t !== task);
    }
    
    const newTaskType = taskTypesArray.join(',');
    setOptimisticUpdates(prev => ({
      ...prev,
      [record.id]: newTaskType
    }));

    if (onRoleChange) {
      onRoleChange(record.id, task, checked);
    }
  };

  const handleAdditionalRoleToggle = async (record, roleType, checked) => {
    // Optimistic update
    const currentTaskType = getTaskType(record.id, record.taskType);
    let taskTypesArray = currentTaskType ? currentTaskType.split(',').map(t => t.trim()).filter(t => t) : [];
    
    if (checked) {
      if (!taskTypesArray.includes(roleType)) {
        taskTypesArray.push(roleType);
      }
    } else {
      taskTypesArray = taskTypesArray.filter(t => t !== roleType);
    }
    
    const newTaskType = taskTypesArray.join(',');
    setOptimisticUpdates(prev => ({
      ...prev,
      [record.id]: newTaskType
    }));

    if (onRoleChange) {
      // เอาค่าที่ "เซิร์ฟเวอร์บันทึกจริง" มาทับค่า optimistic
      // ไม่งั้นถ้าเซฟได้ไม่ครบ (เช่นกดรัว ๆ แล้วชนกัน) หน้าจอจะโชว์ว่าปิดแล้ว
      // ทั้งที่ DB ยังเปิดอยู่ → เคสไหลเข้าต่อโดยไม่มีใครรู้
      const saved = await onRoleChange(record.id, roleType, checked);
      if (saved !== undefined) {
        setOptimisticUpdates((prev) => ({ ...prev, [record.id]: saved || '' }));
      }
    }
  };

  // Reset optimistic updates when data changes (after refetch)
  useEffect(() => {
    if (data && data.length > 0) {
      setOptimisticUpdates({});
    }
  }, [data]);

  const handleExport = (record) => {
    setSelectedRecord(record);
    setExportModalVisible(true);
    setExportType(null);
    setQuantity('');
    setSelectedAdmin(null);
  };

  const handleExportTypeChange = (type,record) => {
    setSelectedRecord(record);
    setExportType(type);
    setQuantity('');
    setSelectedAdmin(null);
  };

  const handleConfirmExport = () => {
    if (!exportType) {
      return;
    }

    if (exportType === 'all') {
      if (!quantity || quantity <= 0) {
        return;
      }
      const exportData = {
        type: 'all',
        quantity: parseInt(quantity),
        record: selectedRecord,
      };
      if (onExport) {
        onExport(exportData);
      }
    } else if (exportType === 'admin') {
      if (!selectedAdmin || !quantity || quantity <= 0) {
        return;
      }
      const exportData = {
        type: 'admin',
        adminId: selectedAdmin,
        quantity: parseInt(quantity),
        record: selectedRecord,
      };
      if (onExport) {
        onExport(exportData);
      }
    }

    setExportModalVisible(false);
    setExportType(null);
    setQuantity('');
    setSelectedAdmin(null);
  };

  const handleCancelExport = () => {
    setExportModalVisible(false);
    setExportType(null);
    setQuantity('');
    setSelectedAdmin(null);
  };

  const columns = [
    {
      title: 'Admin',
      dataIndex: 'userName',
      key: 'userName',
      align: 'center',
      width: 120,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (text) => <span style={{ color: '#333', fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Waiting',
      dataIndex: 'waitingCount',
      key: 'waitingCount',
      align: 'center',
      width: 100,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Pending',
      dataIndex: 'pendingCount',
      key: 'pendingCount',
      align: 'center',
      width: 100,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'ส่งออก',
      key: 'export',
      align: 'center',
      width: 100,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (_, record) => (
        <Button
          type="text"
          //icon={<ArrowRightOutlined />}
          onClick={() => handleExport(record)}
          style={{ borderRadius: 6 }}
        >
          <ExportOutlined />
        </Button>
      ),
    },
    // {
    //   title: 'หน้าที่',
    //   dataIndex: 'MainSchedule',
    //   key: 'MainSchedule',
    //   align: 'center',
    //   width: 120,
    //   onHeaderCell: () => ({ style: headerStyle }),
    //   render: (record,task) => {
    //     const taskType = getTaskType(task.id, task.taskType);
    //     const isChecked = typeof taskType === 'string' && taskType.split(',').map(t => t.trim()).includes(record);
    //     return (
    //       <Space>
    //         <span style={{ marginRight: 8 }}>{record}</span>
    //         <Switch
    //           checked={isChecked}
    //           onChange={(checked) => handleRoleToggle(record,task, checked)}
    //           style={{ borderRadius: 12 }}
    //         />
    //       </Space>
    //     );
    //   },
    // },
    {
      title: 'หน้าที่ (เพิ่มเติม)',
      key: 'additionalRole',
      align: 'center',
      children: [
        {
          title: 'DEP',
          key: 'roleDep',
          align: 'center',
          width: 80,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (_, record) => {
            const taskType = getTaskType(record.id, record.taskType);
            const isChecked = typeof taskType === 'string' && taskType.split(',').map(t => t.trim()).includes('Deposit');
            const isDisabled = typeof record.MainSchedule === 'string' 
              ? record.MainSchedule == "Deposit"
              : false;
            return (
              <Switch 
                checked={isChecked} 
                onChange={(checked) => handleAdditionalRoleToggle(record, 'Deposit', checked)} 
                style={{ borderRadius: 12 }} 
                //disabled={isDisabled}
              />
            );
          },
        },
        {
          title: 'WIT',
          key: 'roleWit',
          align: 'center',
          width: 80,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (_, record) => {
            const taskType = getTaskType(record.id, record.taskType);
            const isChecked = typeof taskType === 'string' && taskType.split(',').map(t => t.trim()).includes('Withdraw');
            const isDisabled = typeof record.MainSchedule === 'string' 
              ? record.MainSchedule == "Withdraw"
              : false;
            return <Switch 
              checked={isChecked} 
              onChange={(checked) => handleAdditionalRoleToggle(record, 'Withdraw', checked)} 
              style={{ borderRadius: 12 }} 
              //disabled={isDisabled}
              />;
          },
        },
        {
          title: 'ETC',
          key: 'roleEtc',
          align: 'center',
          width: 80,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (_, record) => {
            const taskType = getTaskType(record.id, record.taskType);
            const isChecked = typeof taskType === 'string' && taskType.split(',').map(t => t.trim()).includes('ETC');
            const isDisabled = typeof record.MainSchedule === 'string' 
              ? record.MainSchedule == "ETC"
              : false;
            return <Switch checked={isChecked} 
            onChange={(checked) => handleAdditionalRoleToggle(record, 'ETC', checked)} 
            style={{ borderRadius: 12 }} 
            //disabled={isDisabled}
            />;
          },
        },
        {
          title: 'REG',
          key: 'roleReg',
          align: 'center',
          width: 80,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (_, record) => {
            const taskType = getTaskType(record.id, record.taskType);
            const isChecked = typeof taskType === 'string' && taskType.split(',').map(t => t.trim()).includes(REGISTER_TASK);
            return (
              <Switch
                checked={isChecked}
                onChange={(checked) => handleAdditionalRoleToggle(record, REGISTER_TASK, checked)}
                style={{ borderRadius: 12 }}
              />
            );
          },
        },
      ],
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: 'Success',
      dataIndex: 'successCount',
      key: 'successCount',
      align: 'center',
      width: 100,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'อัตราเฉลี่ยการจัดการเคส (นาที.วินาที)',
      key: 'averageRate',
      align: 'center',
      children: [
        {
          title: 'DEP',
          dataIndex: 'averageDeposit',
          key: 'averageDeposit',
          align: 'center',
          width: 120,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (text) => <span style={{ color: '#333' }}>{text}</span>,
        },
        {
          title: 'WIT',
          dataIndex: 'averageWithdraw',
          key: 'averageWithdraw',
          align: 'center',
          width: 100,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (text) => <span style={{ color: '#333' }}>{text}</span>,
        },
        {
          title: 'ETC',
          dataIndex: 'averageETC',
          key: 'averageETC',
          align: 'center',
          width: 100,
          onHeaderCell: () => ({ style: subHeaderStyle }),
          render: (text) => <span style={{ color: '#333' }}>{text}</span>,
        },
      ],
      onHeaderCell: () => ({ style: headerStyle }),
    },
  ];

  return (
    <div style={{ padding: '20px', background: '#fff' }}>
      {/* Header Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <h2 style={{ 
          color: '#1890ff', 
          fontSize: '20px', 
          fontWeight: 'bold', 
          margin: 0 
        }}>
          จัดการ / วิเคราะห์รายบุคคล
        </h2>
       
      </div>

      {/* Table Section */}
      <div className='overflow-x-auto'>
        <AntTable
          loading={loading}
          dataSource={data}
          columns={columns}
          pagination={false}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          bordered
        />
      </div>

      {/* Export Modal */}
      <Modal
        title={
          <span style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#000' 
          }}>
            เลือกรูปแบบส่งออก
          </span>
        }
        open={exportModalVisible}
        onCancel={handleCancelExport}
        footer={null}
        width={500}
      >
          <div style={{ padding: '20px 0' }}>
            {/* Selection Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginBottom: '24px',
            justifyContent: 'flex-start'
          }}>
            <Button
              onClick={() => handleExportTypeChange('all',selectedRecord)}
              style={{
                backgroundColor: exportType === 'all' ? '#1890ff' : '#e0e0e0',
                borderColor: exportType === 'all' ? '#1890ff' : '#d9d9d9',
                color: exportType === 'all' ? '#fff' : '#333',
                borderRadius: '6px',
                padding: '8px 24px',
                height: '40px',
                fontWeight: '500',
                border: 'none',
                boxShadow: exportType === 'all' ? '0 2px 4px rgba(24, 144, 255, 0.3)' : 'none',
                transition: 'all 0.3s',
              }}
            >
              งานทั้งหมด
            </Button>
            <Button
              onClick={() => handleExportTypeChange('admin',selectedRecord)}
              style={{
                backgroundColor: exportType === 'admin' ? '#1890ff' : '#e0e0e0',
                borderColor: exportType === 'admin' ? '#1890ff' : '#d9d9d9',
                color: exportType === 'admin' ? '#fff' : '#333',
                borderRadius: '6px',
                padding: '8px 24px',
                height: '40px',
                fontWeight: '500',
                border: 'none',
                boxShadow: exportType === 'admin' ? '0 2px 4px rgba(24, 144, 255, 0.3)' : 'none',
                transition: 'all 0.3s',
              }}
            >
              Admin
            </Button>
          </div>

          {/* Fields for "งานทั้งหมด" */}
          {exportType === 'all' && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
              }}>
                <label style={{ 
                  minWidth: '60px', 
                  color: '#333',
                  fontWeight: 500
                }}>
                  จำนวน
                </label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="ระบุจำนวน"
                  style={{
                    width: '150px',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                  }}
                />
                <label style={{ color: '#333' }}>เคส</label>
              </div>
            </div>
          )}

          {/* Fields for "Admin" */}
          {exportType === 'admin' && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '16px'
              }}>
                <label style={{ 
                  minWidth: '100px', 
                  color: '#333',
                  fontWeight: 500
                }}>
                  ส่งเคสให้
                </label>
                <Select
                  placeholder="- เลือก -"
                  value={selectedAdmin}
                  onChange={setSelectedAdmin}
                  style={{
                    width: '200px',
                    borderRadius: '6px',
                  }}
                  allowClear
                >
                  {data
                    .filter(admin => admin.userId !== selectedRecord.userId)
                    .map(admin => (
                      <Option key={admin.userId} value={admin.userId}>
                        {admin.userName}
                      </Option>
                    ))
                  }
                </Select>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px'
              }}>
                <label style={{ 
                  minWidth: '100px', 
                  color: '#333',
                  fontWeight: 500
                }}>
                  จำนวน
                </label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="ระบุจำนวน"
                  style={{
                    width: '150px',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                  }}
                />
                <label style={{ color: '#333' }}>เคส</label>
              </div>
            </div>
          )}

          {/* Confirm Button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            marginTop: '24px'
          }}>
            <Button
              onClick={handleConfirmExport}
              disabled={
                !exportType || 
                !quantity || 
                quantity <= 0 || 
                (exportType === 'admin' && !selectedAdmin)
              }
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                borderRadius: '6px',
                padding: '8px 32px',
                height: '40px',
                fontWeight: 'bold',
                color: '#fff',
                width: '100%',
              }}
            >
              ยืนยัน
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Table;
